import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import image1 from "../../../../assets/programme/bannerimage1.jpeg";
import image2 from "../../../../assets/programme/bannerimage2.jpeg";
import FooterInnerPagee from "../../../../common/FooterInnerPagee";
import Header from "../../../../common/Header";
import { useNavigate } from "react-router-dom";
import LeftSideNavigation from "../../../../common/LeftSideNavigation";

const ScrollContainer = styled("div")(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  scrollBehavior: "smooth",
  height: "100%",
  width: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const ScrollItem = styled("div")({
  flex: "0 0 100%",
  scrollSnapAlign: "start",
  height: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
});

const ScrollImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  margin: 0,
  padding: 0,
});

const DotsContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  zIndex: 10,
  pointerEvents: "auto",
}));

const Dot = styled("div")(({ active }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  cursor: "pointer",
  backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "white",
    transform: "scale(1.2)",
  },
}));

const Documentary = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const images = [image2, image1];
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);

  const handleDotClick = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollWidth = container.scrollWidth;
    const itemWidth = scrollWidth / images.length;
    container.scrollTo({ left: itemWidth * index, behavior: "smooth" });
    setCurrentIndex(index);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.offsetWidth;
    const index = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % images.length;
      setCurrentIndex(nextIndex);
      container.scrollTo({
        left: container.clientWidth * nextIndex,
        behavior: "smooth",
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const itemWidth = scrollWidth / images.length;
      const newIndex = Math.round(container.scrollLeft / itemWidth);
      setCurrentIndex(newIndex);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Header />

      {/* Hero Section - scrolls away normally */}
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "50vh" : "65vh",
          position: "relative",
        }}
      >
        <ScrollContainer ref={scrollRef}>
          {images.map((img, index) => (
            <ScrollItem key={index}>
              <ScrollImage src={img} alt={`Banner ${index + 1}`} />
            </ScrollItem>
          ))}
        </ScrollContainer>

        <DotsContainer>
          {images.map((_, idx) => (
            <Dot
              key={idx}
              active={currentIndex === idx}
              onClick={() => handleDotClick(idx)}
            />
          ))}
        </DotsContainer>
      </Box>

      {/* Main Content Section */}
      <Grid container sx={{
        px: isMobile ? 2 : 4,
        py: isMobile ? 2 : 4,
        position: 'relative',
        maxWidth: '100%',
        margin: '0 auto',
      }}>
        {/* Left Side Navigation - Sticky */}
        {!isMobile && (
          <Grid item md={2} sx={{
            position: 'sticky',
            top: 80,
            height: 'calc(100vh - 64px)',
            alignSelf: 'flex-start',
            overflowY: 'auto',
            zIndex: 1,
            pr: 2,
          }}>
            <LeftSideNavigation />
          </Grid>
        )}

        {/* Right Side Content - Scrollable */}
        <Grid item xs={12} md={10} sx={{
          border: '1px solid #7f6b56',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}>
          <Box
            sx={{
              color: "#7f6b56",
              fontFamily: "Inter, sans-serif",
              px: isMobile ? 2 : 3,
              py: isMobile ? 3 : 6,
              position: "relative",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Documentary
            </Typography>

            {/* <Typography
              variant="body1"
              gutterBottom
              sx={{
                mt: 2,
                lineHeight: 1.6,
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "20px",
                fontWeight: "500",
              }}
            >
              This section will showcase our documentary projects about Lakshadweep's marine ecosystems and conservation efforts.
            </Typography> */}

            {/* Add your documentary content here */}
            {/* <Typography
              variant="body1"
              gutterBottom
              sx={{
                mt: 2,
                lineHeight: 1.6,
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "20px",
                fontWeight: "500",
              }}
            >
              Our documentaries capture the beauty of the islands, the challenges they face, and the conservation work being done to protect them.
            </Typography> */}
          </Box>
        </Grid>

        {/* Mobile Navigation - appears after content */}
        {isMobile && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <LeftSideNavigation />
          </Grid>
        )}
      </Grid>

      <FooterInnerPagee />
    </>
  );
};

export default Documentary;